import {
    consulta,
    formulario,
    generarFiltros,
    crear_form_data,
} from '../../../../global/js/funciones';
import {
    normalizarRespuestaProgreso,
    esRespuestaProgresoValida,
} from '../helpers/bienestarFichaUtils';


/**
 * Progreso de la ficha de seguimiento. Aplica la normalización y validación
 * usadas por el componente; devuelve `null` cuando no hay progreso útil.
 */
export const fetchProgresoFichaSeguimiento = ({
    bienestarId,
    tipoServicioCodigo,
    codigoFichaSecundario = '',
}) => new Promise((resolve) => {
    const tipo = String(tipoServicioCodigo || '').trim();
    if (!bienestarId || !tipo) {
        resolve(null);
        return;
    }
    const params = [
        `tipo_servicio_codigo=${encodeURIComponent(tipo)}`,
        `_ts=${Date.now()}`,
    ];
    if (codigoFichaSecundario) {
        params.push(`codigo_ficha_secundario=${encodeURIComponent(codigoFichaSecundario)}`);
    }
    const qs = params.join('&');
    consulta(
        `api/v1.0/bienestar/${bienestarId}/ficha_seguimiento/progreso?${qs}`,
        null,
        'get',
        (error, estado, resp) => {
            const progresoNormalizado = normalizarRespuestaProgreso(resp, codigoFichaSecundario);
            if (!error && estado === 200 && esRespuestaProgresoValida(progresoNormalizado)) {
                resolve(progresoNormalizado);
                return;
            }
            resolve(null);
        }
    );
});


/** Detalle de un bienestar por id. */
export const fetchBienestarDetallePorId = async (id) => {
    if (!id) return null;
    const f = await generarFiltros([{ llave: 'id', valor: id }]);
    return new Promise((resolve) => {
        consulta(`api/v1.0/bienestar/detalle?${f}`, null, null, (error, estado, resp) => {
            resolve(!error && estado === 200 ? resp : null);
        });
    });
};


/** Lista de estados de un bienestar. */
export const fetchEstadosBienestar = async (bienestarId) => {
    if (!bienestarId) return null;
    const f = await generarFiltros([{ llave: 'bienestar', valor: bienestarId }]);
    return new Promise((resolve) => {
        consulta(`api/v1.0/bienestar/estados?${f}`, null, null, (error, estado, resp) => {
            resolve(estado === 200 ? resp : null);
        });
    });
};


/** Observaciones de un bienestar. Devuelve siempre array (vacío si error). */
export const fetchObservacionesBienestar = (idBienestar) => new Promise((resolve) => {
    consulta(`api/v1.0/bienestar/${idBienestar}/observaciones`, null, null, (error, status, resp) => {
        resolve(status === 200 && Array.isArray(resp) ? resp : []);
    });
});


/** Historial de bienestares de un estudiante por id de usuario. */
export const fetchHistorialEstudiante = (usuarioId) => new Promise((resolve) => {
    consulta(
        `api/v1.0/bienestar_estudiantil/${usuarioId}/historial_estudiante_bienestar`,
        null,
        null,
        (error, status, resp) => {
            resolve(status === 200 && Array.isArray(resp) ? resp : []);
        },
    );
});


/**
 * Adjuntos de un bienestar. Si `tipoAdjunto` se omite trae todos.
 * Devuelve `null` si falla la consulta para que el caller distinga error de "vacío".
 */
export const fetchAdjuntosBienestar = (idBienestar, tipoAdjunto = null) => new Promise((resolve) => {
    let url = `api/v1.0/bienestar/${idBienestar}/adjuntos`;
    if (tipoAdjunto) url += `?tipo_adjunto=${tipoAdjunto}`;
    consulta(url, null, null, (error, status, resp) => {
        if (status === 200) {
            resolve(Array.isArray(resp) ? resp : []);
            return;
        }
        resolve(null);
    });
});


/** Adjuntos de tipo 1 (documentos generados) usados al armar el listado de fichas. */
export const fetchAdjuntosFicha = (bienestarId) => new Promise((resolve) => {
    consulta(
        `api/v1.0/bienestar/${bienestarId}/adjuntos?tipo=1`,
        null,
        'get',
        (error, estado, resp) => {
            resolve(!error && estado === 200 && Array.isArray(resp) ? resp : []);
        },
    );
});


/** Solicitudes de bienestar filtradas. Devuelve la respuesta cruda (o null). */
export const fetchSolicitudesBienestar = async (filtros = []) => {
    const f = await generarFiltros(filtros);
    return new Promise((resolve) => {
        consulta(`api/v1.0/bienestar_estudiantil/listar/0?${f}`, null, null, (error, estado, resp) => {
            resolve(estado === 200 && !error ? resp : null);
        });
    });
};


const consultarSimpleGet = (url) => new Promise((resolve) => {
    consulta(url, null, 'get', (_, __, resp) => resolve(resp));
});


export const fetchFormulariosCompletadosBienestar = async (bienestarId) => {
    if (!bienestarId) return null;
    const [
        asistencias,
        disentimiento,
        disentimientoEps,
        satisfaccion,
        consentimientoGeneral,
        consentimientoCrisis,
        adjuntosGenerados,
    ] = await Promise.all([
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/asistencias`),
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/disentimiento`),
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/disentimiento_traslado_eps`),
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/satisfaccion`),
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/consentimientos/general`),
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/consentimientos/crisis`),
        consultarSimpleGet(`api/v1.0/bienestar/${bienestarId}/adjuntos?tipo_adjunto=1&omitir_permiso_documentos=1`),
    ]);
    return {
        asistencias,
        disentimiento,
        disentimientoEps,
        satisfaccion,
        consentimientoGeneral,
        consentimientoCrisis,
        adjuntosGenerados,
    };
};


/**
 * POST de confirmación/rechazo de remisión.
 * @returns {Promise<{ ok: boolean, error: any, estado: number, resp: any }>}
 */
export const enviarRemisionConfirmacion = async (pk, accion) => {
    const data = { accion };
    const form = await crear_form_data(data);
    return new Promise((resolve) => {
        formulario(
            `api/v1.0/bienestar_estudiantil/${pk}/remision_confirmacion`,
            form,
            'post',
            (error, estado, resp) => {
                resolve({ ok: !error && estado === 200, error, estado, resp });
            },
        );
    });
};


/**
 * Asignar (o reasignar) un profesional a una solicitud de bienestar.
 * Body: { profesional_id }
 * Resuelve { ok, error, estado, resp }.
 * El backend incluye en `resp.profesional_anterior` el profesional previo
 * cuando se trata de una reasignación (null en la asignación inicial).
 */
export const fetchAsignarProfesional = (bienestarId, profesionalId) => new Promise((resolve) => {
    if (!bienestarId || !profesionalId) {
        resolve({ ok: false, error: 'invalid_params', estado: 400, resp: null });
        return;
    }
    const url = `api/v1.0/bienestar/${bienestarId}/asignar_profesional/`;
    const data = { profesional_id: profesionalId };
    consulta(url, data, 'post', (error, estado, resp) => {
        resolve({ ok: !error && estado === 200, error, estado, resp });
    });
});


// Datos de bienestar sicuc
export const fetchDatosBienestarSICUC = () => new Promise((resolve) => {
    const url = `api/v1.0/bienestar/datos_bienestar`;
    consulta(url, null, 'get', (error, estado, resp) => {
        resolve(!error && estado === 200 ? resp : null);
    });
});


/** Datos del usuario autenticado para bienestar estudiantil. */
export const fetchDatosPreviosEstudiante = (bienestarId) => new Promise((resolve) => {
    if (!bienestarId) {
        resolve({});
        return;
    }
    consulta(`api/v1.0/bienestar/${bienestarId}/datos_previos`, null, null, (error, estado, resp) => {
        resolve(!error && estado === 200 && resp?.datos ? resp.datos : {});
    });
});


export const fetchDatosUsuarioBienestar = () => new Promise((resolve) => {
    consulta(`api/v1.0/bienestar_estudiantil/datos_usuario`, null, null, (error, estado, resp) => {
        resolve(resp);
    });
});


/** Crear solicitud de bienestar estudiantil. */
export const postCrearSolicitudBienestar = async (payload) => {
    const form = await crear_form_data(payload);
    return new Promise((resolve) => {
        formulario(
            `api/v1.0/bienestar_estudiantil/crear`,
            form,
            'post',
            (error, estado, resp) => {
                resolve({ error, estado, resp });
            },
        );
    });
};


/** Periodos disponibles para bienestar estudiantil. */
export const fetchPeriodosBienestar = () => new Promise((resolve) => {
    consulta(`api/v1.0/bienestar_estudiantil/periodos`, null, null, (error, estado, resp) => {
        if (estado === 200 && !error && Array.isArray(resp)) {
            resolve(resp);
        } else {
            resolve([]);
        }
    });
});


/** Gestores de bienestar estudiantil. */
export const fetchGestoresBienestar = () => new Promise((resolve) => {
    consulta(`api/v1.0/bienestar/encargados`, null, null, (error, estado, resp) => {
        resolve(estado === 200 && !error ? resp : null);
    });
});


/** Subir adjuntos de bienestar con FormData. */
export const postAdjuntosBienestar = (bienestarId, form) => new Promise((resolve) => {
    formulario(
        `api/v1.0/bienestar/${bienestarId}/adjuntos/crear`,
        form,
        'post',
        (error, estado, resp) => {
            resolve({ error, estado, resp });
        },
    );
});


/** Profesionales por perfil para bienestar. */
export const fetchProfesionalesBienestar = () => new Promise((resolve) => {
    consulta('api/v1.0/bienestar/personas_por_perfil', null, 'get', (error, __, resp) => {
        if (error) {
            resolve([]);
            return;
        }
        resolve(resp?.results || resp?.data || resp || []);
    });
});


/** Enviar formatos de bienestar. */
export const postFormatoBienestar = async (bienestarId, tipoFormulario, payload) => {
    const form = await crear_form_data(payload);
    let endpoint = '';
    if (tipoFormulario === 'asistencia') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/asistencias/crear`;
    } else if (tipoFormulario === 'satisfaccion') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/satisfaccion/crear`;
    } else if (tipoFormulario === 'consentimiento') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/consentimientos/general/crear`;
    } else if (tipoFormulario === 'consentimiento_crisis') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/consentimientos/crisis/crear`;
    } else if (tipoFormulario === 'disentimiento') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/disentimiento/crear`;
    } else if (tipoFormulario === 'disentimiento_traslado_eps') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/disentimiento_traslado_eps/crear`;
    }

    return new Promise((resolve) => {
        formulario(endpoint, form, 'post', (error, estado, resp) => {
            resolve({ error, estado, resp });
        });
    });
};


/** Sesiones de bienestar por id. */
export const fetchSesionesBienestar = (bienestarId) => new Promise((resolve) => {
    consulta(`api/v1.0/bienestar/${bienestarId}`, null, null, (error, estado, resp) => {
        if (estado === 200 && resp && Array.isArray(resp.sesiones)) {
            resolve(resp.sesiones);
        } else {
            resolve([]);
        }
    });
});


/** Detalle de formulario por sesion. */
export const fetchDetalleFormularioSesion = (bienestarId, tipoFormulario, sesionNumero) => {
    let endpoint = '';
    if (tipoFormulario === 'asistencia') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/asistencias?sesion_numero=${sesionNumero}`;
    } else if (tipoFormulario === 'satisfaccion') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/satisfaccion?sesion_numero=${sesionNumero}`;
    } else if (tipoFormulario === 'consentimiento') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/consentimientos/general?sesion_numero=${sesionNumero}`;
    } else if (tipoFormulario === 'consentimiento_crisis') {
        endpoint = `api/v1.0/bienestar/${bienestarId}/consentimientos/crisis?sesion_numero=${sesionNumero}`;
    }

    return new Promise((resolve) => {
        if (!endpoint) {
            resolve([]);
            return;
        }
        consulta(endpoint, null, null, (error, estado, resp) => {
            if (!error && estado === 200 && Array.isArray(resp)) {
                resolve(resp);
            } else {
                resolve([]);
            }
        });
    });
};


/** Detalle de bienestar con fallback por id directo. */
export const fetchBienestarDetalleSeguro = async (id) => {
    if (!id) return null;
    try {
        const f = await generarFiltros([{ llave: 'id', valor: id }]);
        const resp = await new Promise((resolve) => {
            consulta(`api/v1.0/bienestar/detalle?${f}`, null, null, (error, estado, data) => {
                resolve(!error && estado === 200 ? data : null);
            });
        });
        if (resp) return resp;
    } catch (err) {
        // continue to fallback
    }

    return new Promise((resolve) => {
        consulta(`api/v1.0/bienestar/detalle?id=${id}`, null, null, (error, estado, data) => {
            resolve(!error && estado === 200 ? data : null);
        });
    });
};


/** Progreso de ficha de seguimiento (respuesta raw). */
export const fetchProgresoFichaSeguimientoRaw = ({
    bienestarId,
    tipoServicioCodigo,
    codigoFichaSecundario = '',
}) => new Promise((resolve) => {
    const tipo = String(tipoServicioCodigo || '').trim();
    if (!bienestarId || !tipo) {
        resolve({ ok: false, estado: 400, resp: null });
        return;
    }
    const params = [
        `tipo_servicio_codigo=${encodeURIComponent(tipo)}`,
    ];
    if (codigoFichaSecundario) {
        params.push(`codigo_ficha_secundario=${encodeURIComponent(codigoFichaSecundario)}`);
    }
    const qs = params.join('&');
    consulta(
        `api/v1.0/bienestar/${bienestarId}/ficha_seguimiento/progreso?${qs}`,
        null,
        null,
        (error, estado, resp) => {
            resolve({ ok: !error && estado === 200, estado, resp });
        },
    );
});


/** Guardar progreso de ficha de seguimiento. */
export const postProgresoFichaSeguimiento = (bienestarId, payload) => new Promise((resolve) => {
    consulta(
        `api/v1.0/bienestar/${bienestarId}/ficha_seguimiento/progreso`,
        payload,
        'POST',
        (error, estado, resp) => {
            resolve({ error, estado, resp });
        },
    );
});
